import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useBrpClubDealer, useDealer } from 'services/Data/DealerServices';
import { useCreateShowroomProjectRequest } from 'services/Data/ShowroomProjectRequestServices';
import QuickDealerChangeModal from '@acnb/CustomComponents/DealerCard/QuickDealerChangeModal';
import { IsClub } from 'config/config';
import DealerContactStep from './Component/DealerContactStep';
import DsmDiscussionStep from './Component/DsmDiscussionStep';
import ProjectDetailsStep from './Component/ProjectDetailsStep';

const initialForm = {
  primaryContactName: '',
  primaryContactEmail: '',
  primaryContactPhone: '',
  plansToRelocateWithinTwoYears: null,
  hasDiscussedWithDsm: null,
  dsmDiscussionDetails: '',
  hasSeaDooSwitch: false,
  hasThreeWheelVehicle: false,
  hasCanAmMotorcycle: false,
  hasSkiDoo: false,
  hasSeaDooPwc: false,
  hasAtv: false,
  hasSideBySide: false,
  totalBrpZoneSquareFootage: '',
  hasCurrentBlueprintsAndPictures: null,
};

const maximumFileCount = 10;
const maximumFileSize = 50 * 1024 * 1024;

const productLines = [
  ['hasSeaDooSwitch', 'Sea-Doo Switch', 'offersBt'],
  ['hasThreeWheelVehicle', '3WV', 'offersRds'],
  ['hasCanAmMotorcycle', 'Can-Am Motorcycle', 'offersTwoWheel'],
  ['hasSkiDoo', 'Ski-Doo', 'offersSno'],
  ['hasSeaDooPwc', 'Sea-Doo (PWC)', 'offersPwc'],
  ['hasAtv', 'ATV', 'offersAtv'],
  ['hasSideBySide', 'SxS', 'offersSsv'],
];

const normalizePhoneNumber = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(0, 10);
};

const ShowroomProjectRequestContent = ({ dealerQuery }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: dealer, isLoading } = dealerQuery;
  const { mutateAsync: createRequest, isPending } = useCreateShowroomProjectRequest();
  const currentDealerId = useRef(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showDealerSelectModal, setShowDealerSelectModal] = useState(false);
  const steps = [
    { formStep: 0, label: t('showroomProjectRequest.stepContact') },
    ...(form.hasDiscussedWithDsm === false ? [] : [{ formStep: 1, label: t('showroomProjectRequest.stepDiscussion') }]),
    { formStep: 2, label: t('showroomProjectRequest.stepProject') },
  ];
  const activeStep = steps.findIndex(({ formStep }) => formStep === step);

  useEffect(() => {
    if (!dealer || currentDealerId.current === dealer.id) return;

    const selectedProductLines = productLines.reduce(
      (selected, [field, , dealerOfferField]) => ({
        ...selected,
        [field]: Boolean(dealer[dealerOfferField]),
      }),
      {},
    );

    setForm({
      ...initialForm,
      ...selectedProductLines,
    });
    setStep(0);
    setFiles([]);
    setErrors({});
    setSubmitted(false);
    currentDealerId.current = dealer.id;
  }, [dealer]);

  const update = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: false }));
  };

  const validateStep = currentStep => {
    const nextErrors = {};
    if (currentStep === 0) {
      nextErrors.primaryContactName = !form.primaryContactName.trim();
      nextErrors.primaryContactEmail = !form.primaryContactEmail.trim();
      nextErrors.primaryContactPhone = form.primaryContactPhone.replace(/\D/g, '').length !== 10;
      nextErrors.plansToRelocateWithinTwoYears = form.plansToRelocateWithinTwoYears === null;
      nextErrors.hasDiscussedWithDsm = form.hasDiscussedWithDsm === null;
    } else if (currentStep === 1) {
      nextErrors.dsmDiscussionDetails = !form.dsmDiscussionDetails.trim();
    } else {
      nextErrors.productLines = !productLines.some(([field]) => form[field]);
      nextErrors.totalBrpZoneSquareFootage = Number(form.totalBrpZoneSquareFootage) <= 0;
      nextErrors.hasCurrentBlueprintsAndPictures = form.hasCurrentBlueprintsAndPictures === null;
      nextErrors.files = files.length > maximumFileCount || files.some(file => file.size > maximumFileSize);
    }

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep(step === 0 && form.hasDiscussedWithDsm === false ? 2 : step + 1);
  };

  const back = () => setStep(step === 2 && form.hasDiscussedWithDsm === false ? 0 : step - 1);

  const selectFiles = event => {
    const additions = Array.from(event.target.files || []);
    const nextFiles = [...files, ...additions];
    const hasInvalidFiles = nextFiles.length > maximumFileCount || additions.some(file => file.size > maximumFileSize);

    if (hasInvalidFiles) {
      setErrors(current => ({ ...current, files: true }));
      event.target.value = '';
      return;
    }

    setFiles(nextFiles);
    setErrors(current => ({ ...current, files: false }));
    event.target.value = '';
  };

  const submit = async () => {
    if (!validateStep(2)) return;
    await createRequest({
      request: { ...form, totalBrpZoneSquareFootage: Number(form.totalBrpZoneSquareFootage) },
      files,
    });
    setSubmitted(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (submitted) {
    const dealerName = dealer?.customerName || t('dealer');
    const dealerNumber = dealer?.identifier;
    const dealerPhone = dealer?.phoneNumber;
    const dealerLocation = [dealer?.city, dealer?.region?.internalName, dealer?.postalCode].filter(Boolean).join(', ');

    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 490px)',
          px: 3,
          py: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <QuickDealerChangeModal show={showDealerSelectModal} onClose={() => setShowDealerSelectModal(false)} />
        <Card
          sx={{
            width: '100%',
            maxWidth: 680,
            borderRadius: 4,
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: 5,
            textAlign: 'center',
          }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h2" sx={{ mb: 2.5 }}>
              {t('showroomProjectRequest.successTitle')}
            </Typography>

            {IsClub && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    border: '1px solid #eceef3',
                    borderRadius: 2,
                    backgroundColor: '#f7f9fc',
                    p: 1.5,
                    textAlign: 'left',
                    maxWidth: 560,
                    mx: 'auto',
                    mb: 1.5,
                  }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.75, textAlign: 'center' }}>
                    {t('verifyDealer')}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
                    {dealerName}
                    {dealerNumber ? ` #${dealerNumber}` : ''}
                  </Typography>
                  {dealerLocation && (
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
                      {t('address')}: {dealerLocation}
                    </Typography>
                  )}
                  {dealerPhone && (
                    <Typography sx={{ mt: 0.25, fontSize: 13, color: 'text.secondary' }}>
                      {t('appModule.phone')}: {dealerPhone}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  disableElevation
                  sx={{ px: 4, py: 1.25, borderRadius: 0, color: '#000', fontWeight: 700 }}
                  onClick={() => setShowDealerSelectModal(true)}>
                  {t('notYourDealer')}
                </Button>
              </Box>
            )}

            <Box sx={{ pt: IsClub ? 2.5 : 0, borderTop: IsClub ? theme => `1px solid ${theme.palette.divider}` : 'none' }}>
              <Alert severity="success" sx={{ maxWidth: 560, mx: 'auto', textAlign: 'left' }}>
                {t('showroomProjectRequest.success')}
              </Alert>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  disableElevation
                  sx={{ px: 4, py: 1.25, borderRadius: 0, color: '#000' }}
                  onClick={() => history.push('/dealer/home-page')}>
                  {t('showroomProjectRequest.home')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', py: { xs: 3, md: 6 }, px: 2 }}>
      <Typography variant="h1" sx={{ mb: 1 }}>
        {t('showroomProjectRequest.title')}
      </Typography>
      <Typography sx={{ mb: 3 }}>{t('showroomProjectRequest.intro')}</Typography>
      <Paper
        elevation={4}
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          mb: { xs: 3, md: 4 },
        }}>
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          alternativeLabel={!isMobile}
          sx={{
            '& .MuiStepLabel-root .Mui-completed': { color: theme.palette.primary.main },
            '& .MuiStepLabel-label': {
              color: theme.palette.text.primary,
              fontSize: isMobile ? '0.85rem' : '1rem',
              '&.Mui-active': { color: theme.palette.primary.main, fontWeight: 700 },
              '&.Mui-completed': { color: theme.palette.text.disabled },
            },
            '& .MuiStepIcon-root': {
              color: theme.palette.action.disabled,
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              '&.Mui-active, &.Mui-completed': { color: theme.palette.primary.main },
            },
            '& .MuiStepConnector-line': { borderColor: theme.palette.divider },
          }}>
          {steps.map(({ formStep, label }, index) => (
            <Step key={formStep} completed={index < activeStep}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Card>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          {step === 0 && <DealerContactStep dealer={dealer} form={form} errors={errors} update={update} />}

          {step === 1 && <DsmDiscussionStep form={form} errors={errors} update={update} />}

          {step === 2 && (
            <ProjectDetailsStep
              form={form}
              errors={errors}
              update={update}
              files={files}
              setFiles={setFiles}
              selectFiles={selectFiles}
              productLines={productLines}
              maximumFileCount={maximumFileCount}
            />
          )}
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button variant="outlined" disabled={step === 0 || isPending} onClick={back}>
          {t('showroomProjectRequest.back')}
        </Button>
        {step < 2 ? (
          <Button variant="contained" onClick={next}>
            {t('showroomProjectRequest.next')}
          </Button>
        ) : (
          <Button variant="contained" disabled={isPending} onClick={submit}>
            {isPending ? <CircularProgress size={24} /> : t('showroomProjectRequest.submit')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const BrpClubShowroomProjectRequest = () => {
  const activeDealerId = useSelector(
    state => state.AccountReducer?.profile?.temporaryDealer_Id || state.AccountReducer?.profile?.dealer_Id,
  );
  const dealerQuery = useBrpClubDealer(activeDealerId);

  return <ShowroomProjectRequestContent dealerQuery={dealerQuery} />;
};

const StandardShowroomProjectRequest = () => {
  const activeDealerId = useSelector(
    state => state.AccountReducer?.profile?.temporaryDealer_Id || state.AccountReducer?.profile?.dealer_Id,
  );
  const dealerQuery = useDealer(activeDealerId);

  return <ShowroomProjectRequestContent dealerQuery={dealerQuery} />;
};

const ShowroomProjectRequest = () => (IsClub ? <BrpClubShowroomProjectRequest /> : <StandardShowroomProjectRequest />);

export default ShowroomProjectRequest;
